/**
 * useCycleWorkflowBehavior Hook
 * ==============================
 *
 * Fetches and caches the resolved cycle-behaviour rules for a specific
 * (programId, cycleNumber) pair from the CycleRuleEngine backend.
 *
 * Usage:
 *   const { behavior, isLoading, isFocEligible, shouldHideMonetary } =
 *     useCycleWorkflowBehavior({ programId, cycleNumber });
 *
 * HARD-STOP: This hook provides DISPLAY configuration only.
 * Callers MUST NOT use the returned values to compute any financial figures.
 */

import { useQuery } from '@tanstack/react-query';
import { cycleWorkflowBehaviorService } from '../services/cycleWorkflowBehaviorService';
import {
  type CycleBehaviorResult,
  type CycleInvoiceDuplicateCheckResult,
  shouldHideMonetaryFields,
  isInvoiceUploadPending,
} from '../types/cycleWorkflowBehavior';

// ─────────────────────────────────────────────────────────────────────────────
// Query Key Factory
// ─────────────────────────────────────────────────────────────────────────────

export const cycleWorkflowBehaviorKeys = {
  all: ['cycleWorkflowBehavior'] as const,
  behavior: (programId: string, cycleNumber: number) =>
    [...cycleWorkflowBehaviorKeys.all, 'behavior', programId, cycleNumber] as const,
  duplicateCheck: (programId: string, caseId: string, cycleNumber: number) =>
    [...cycleWorkflowBehaviorKeys.all, 'duplicateCheck', programId, caseId, cycleNumber] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useCycleWorkflowBehavior
// ─────────────────────────────────────────────────────────────────────────────

interface UseCycleWorkflowBehaviorOptions {
  programId: string;
  cycleNumber: number;
  /** Set false to pause the query (e.g. while a form is loading). */
  enabled?: boolean;
}

interface UseCycleWorkflowBehaviorResult {
  /** Fully resolved behavior from the server. Null while loading or on error. */
  behavior: CycleBehaviorResult | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;

  // ── Derived convenience flags ────────────────────────────────────────────

  /** True when monetary/financial fields should be hidden. */
  shouldHideMonetary: boolean;

  /** True when the patient is in the invoice-validation phase. */
  isInvoiceValidationPhase: boolean;

  /** True when the patient has become FOC-eligible. */
  isFocEligible: boolean;

  /** True when an invoice document upload is required for this cycle. */
  invoiceUploadRequired: boolean;

  /** Resolved workflow mode string (Standard | InvoiceValidation | FocAssistance). */
  workflowMode: CycleBehaviorResult['workflowMode'] | null;
}

export function useCycleWorkflowBehavior({
  programId,
  cycleNumber,
  enabled = true,
}: UseCycleWorkflowBehaviorOptions): UseCycleWorkflowBehaviorResult {
  const queryEnabled = enabled && !!programId && cycleNumber >= 1;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: cycleWorkflowBehaviorKeys.behavior(programId, cycleNumber),
    queryFn: () => cycleWorkflowBehaviorService.getCycleBehavior(programId, cycleNumber),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — config rarely changes mid-session
    gcTime: 10 * 60 * 1000,
  });

  const behavior = data ?? null;

  return {
    behavior,
    isLoading,
    isError,
    error: error as Error | null,
    shouldHideMonetary: shouldHideMonetaryFields(behavior),
    isInvoiceValidationPhase: behavior?.isInvoiceValidationPhase ?? false,
    isFocEligible: behavior?.isFocEligible ?? false,
    invoiceUploadRequired: behavior?.invoiceUploadRequired ?? false,
    workflowMode: behavior?.workflowMode ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useCycleInvoiceDuplicateCheck
// ─────────────────────────────────────────────────────────────────────────────

interface UseCycleInvoiceDuplicateCheckOptions {
  programId: string;
  caseId: string;
  cycleNumber: number;
  /** Only run the check when the user is about to upload an invoice. */
  enabled?: boolean;
}

interface UseCycleInvoiceDuplicateCheckResult {
  duplicateCheck: CycleInvoiceDuplicateCheckResult | null;
  isLoading: boolean;
  isError: boolean;
  hasDuplicate: boolean;
}

export function useCycleInvoiceDuplicateCheck({
  programId,
  caseId,
  cycleNumber,
  enabled = true,
}: UseCycleInvoiceDuplicateCheckOptions): UseCycleInvoiceDuplicateCheckResult {
  const queryEnabled = enabled && !!programId && !!caseId && cycleNumber >= 1;

  const { data, isLoading, isError } = useQuery({
    queryKey: cycleWorkflowBehaviorKeys.duplicateCheck(programId, caseId, cycleNumber),
    queryFn: () =>
      cycleWorkflowBehaviorService.checkDuplicateCycleInvoice(programId, caseId, cycleNumber),
    enabled: queryEnabled,
    staleTime: 30 * 1000, // 30 seconds — check freshness is important before upload
  });

  return {
    duplicateCheck: data ?? null,
    isLoading,
    isError,
    hasDuplicate: data?.hasDuplicate ?? false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useCycleInvoiceUploadStatus — convenience hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Combines behavior lookup + duplicate check into a single hook
 * for the invoice upload flow.
 */
export function useCycleInvoiceUploadStatus({
  programId,
  caseId,
  cycleNumber,
  hasUploadedInvoice,
  enabled = true,
}: {
  programId: string;
  caseId: string;
  cycleNumber: number;
  hasUploadedInvoice: boolean;
  enabled?: boolean;
}) {
  const behavior = useCycleWorkflowBehavior({ programId, cycleNumber, enabled });
  const duplicate = useCycleInvoiceDuplicateCheck({
    programId,
    caseId,
    cycleNumber,
    enabled: enabled && behavior.invoiceUploadRequired,
  });

  return {
    ...behavior,
    hasDuplicate: duplicate.hasDuplicate,
    duplicateCheck: duplicate.duplicateCheck,
    isDuplicateCheckLoading: duplicate.isLoading,
    canUploadInvoice:
      behavior.invoiceUploadRequired &&
      !duplicate.hasDuplicate &&
      !duplicate.isLoading,
    isInvoicePending: isInvoiceUploadPending(behavior.behavior, hasUploadedInvoice),
  };
}
